import React from 'react';
import './SpinalCordReference.css';

const SCReference = () => {
    return (
        <div className="section">
            <div className="main-contents">
                <div className="left-column-scr">
                    <label className='scr-head'>Muscle Function Grading</label>
                    <div className="sec">
                        <div className="grading-item">
                            <strong>0</strong> = total paralysis
                        </div>
                        <div className="grading-item">
                            <strong>1</strong> = palpable or visible contraction
                        </div>
                        <div className="grading-item">
                            <strong>2</strong> = active movement, full range of motion (ROM) with gravity eliminated
                        </div>
                        <div className="grading-item">
                            <strong>3</strong> = active movement, full ROM against gravity
                        </div>
                        <div className="grading-item">
                            <strong>4</strong> = active movement, full ROM against gravity and moderate resistance in a muscle specific position
                        </div>
                        <div className="grading-item">
                            <strong>5</strong> = (normal) active movement, full ROM against gravity and full resistance in a functional muscle position expected from an otherwise unimpaired person
                        </div>
                        <div className="grading-item">
                            <strong>5*</strong> = (normal) active movement, full ROM against gravity and sufficient resistance to be considered normal if identified inhibiting factors (i.e. pain, disuse) were not present
                        </div>
                        <div className="grading-item">
                            <strong>NT</strong> = not testable (i.e. due to immobilization, severe pain such that the patient cannot be graded, amputation of limb, or contracture of  50% of the normal ROM)
                        </div>
                    </div>

                    <div className="sec">
                        <h2>Sensory Grading</h2>
                        <div className="grading-item">
                            <strong>0</strong> = Absent
                        </div>
                        <div className="grading-item">
                            <strong>1</strong> = Altered, either decreased/impaired sensation or hypersensitivity
                        </div>
                        <div className="grading-item">
                            <strong>2</strong> = Normal
                        </div>
                        <div className="grading-item">
                            <strong>NT</strong> = Not testable
                        </div>
                    </div>
                    <div className="sec">
                        <h2>When to Test Non-Key Muscles:</h2>
                        <p>In a patient with an apparent AIS B classification, non-key muscle functions more than 3 levels below the motor level on each side should be tested to most accurately classify the injury (differentiate between AIS B and C).</p>

                        <div className="subsec">
                            <h3>Movement</h3>
                            <h3>Root Level</h3>
                        </div>
                    </div>
                    <div className="sec">
                        <div className="root-levels">
                            <div className="level-group">
                                <div className="level-item">
                                    <span><strong>Shoulder:</strong> Flexion, extension, abduction, adduction, internal and external rotation</span>
                                    <span><strong>Elbow:</strong> Supination</span>
                                </div>
                                <span className="level">C5</span>
                            </div>
                            <div className="level-group">
                                <div className="level-item">
                                    <span><strong>Elbow:</strong> Pronation</span>
                                    <span><strong>Wrist:</strong> flexion</span>
                                </div>
                                <span className="level">C6</span>
                            </div>
                            <div className="level-group">
                                <div className="level-item">
                                    <span><strong>Finger:</strong> Flexion at proximal joint, extension</span>
                                    <span><strong>Thumb:</strong> Flexion, extension and abduction in plane of thumb</span>
                                </div>
                                <span className="level">C7</span>
                            </div>
                            <div className="level-group">
                                <div className="level-item">
                                    <span><strong>Finger:</strong> Flexion at MCP joint</span>
                                    <span><strong>Thumb:</strong> Opposition, adduction and abduction perpendicular to palm</span>
                                </div>
                                <span className="level">T1</span>
                            </div>
                            <div className="level-group">
                                <div className="level-item">
                                    <span> <strong>Finger:</strong> Abduction of the index finger</span>
                                </div>
                                <span className="level">L1</span>
                            </div>
                            <div className="level-group">
                                <div className="level-item">
                                    <span> <strong>Hip:</strong> Adduction</span>
                                </div>
                                <span className="level">L2</span>
                            </div>
                            <div className="level-group">
                                <div className="level-item">
                                    <span> <strong>Hip:</strong> External rotation</span>
                                </div>
                                <span className="level">L3</span>
                            </div>
                            <div className="level-group">
                                <div className="level-item">
                                    <span> <strong>Hip:</strong> Extension, adduction, internal rotation</span>
                                    <span> <strong>Knee:</strong> Flexion</span>
                                    <span><strong>Ankle:</strong> Inversion and eversion</span>
                                    <span> <strong>Toe:</strong> MP and IP extension</span>
                                </div>
                                <span className="level">L4</span>
                            </div>
                            <div className="level-group">
                                <div className="level-item">
                                    <span><strong>Hallux and Toe:</strong> DIP and PIP flexion and abduction</span>
                                </div>
                                <span className="level">L5</span>
                            </div>
                            <div className="level-group">
                                <div className="level-item">
                                    <span><strong>Hallux:</strong> Abduction</span>
                                </div>
                                <span className="level">S1</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="center-column-scr">
                    <label className='scr-head'>ASIA Impairment Scale (AIS)</label>
                    <div className="sec-scr sec">
                        <div className="ais-item">
                            <div className="ais-content">
                                <strong>A = Complete.</strong> No sensory or motor function is preserved in the sacral segments S4-5.
                            </div>
                        </div>

                        <div className="ais-item">
                            <div className="ais-content">
                                <strong>B = Sensory incomplete.</strong> Sensory but not motor function is preserved below the neurological level and includes the sacral segments S4-5 (light touch or pin prick at S4-5 or deep anal pressure) AND no motor function is preserved more than three levels below the motor level on either side of the body.
                            </div>
                        </div>

                        <div className="ais-item">
                            <div className="ais-content">
                                <strong>C = Motor incomplete.</strong> Motor function is preserved at the most caudal sacral segments for voluntary anal contraction (VAC) OR the patient meets the criteria for sensory incomplete status (sensory function preserved at the most caudal sacral segments [S4-S5] by LT, PP or DAP), and has some sparing of motor function more than three levels below the ipsilateral motor level on either side of the body.
                                <br /><br />
                                (This includes key or non-key muscle functions to determine motor incomplete status.) For AIS C = less than half of key muscle functions below the single NLI have a muscle grade ≥ 3.
                            </div>
                        </div>

                        <div className="ais-item">
                            <div className="ais-content">
                                <strong>D = Motor incomplete.</strong> Motor incomplete status as defined above, with at least half but not more of key muscle functions below the single NLI having a muscle grade ≥ 3.
                            </div>
                        </div>

                        <div className="ais-item">
                            <div className="ais-content">
                                <strong>E = Normal.</strong> If sensation and motor function as tested with the ISNCSCI are graded as normal in all segments, and the patient had prior deficits, then the AIS grade is E. Someone without an initial SCI does not receive an AIS grade.
                            </div>
                        </div>

                        <div className="using-nd">
                            <strong>Using ND:</strong> To document the sensory, motor and NLI levels, the ASIA Impairment Scale grade, and/or the zone of partial preservation (ZPP) when they are unable to be determined based on the examination results.
                        </div>
                    </div>

                    <div className="logos">
                        <div className="asia-logo">
                            <div className="logo-text">ASIA</div>
                            <div className="logo-subtitle">AMERICAN SPINAL INJURY ASSOCIATION</div>
                        </div>
                        <div className="standards-text">
                            <strong>INTERNATIONAL STANDARDS FOR NEUROLOGICAL CLASSIFICATION OF SPINAL CORD INJURY</strong>
                        </div>
                        <div className="iscos-logo">
                            <div className="iscos-text">ISCoS</div>
                        </div>
                    </div>
                </div>

                <div className="right-column-scr">
                    <label className='scr-head'>Steps in Classification</label>
                    <div className="sec">
                        <p className="intro-text">The following order is recommended for determining the classification of individuals with SCI.</p>

                        <div className="step">
                            <h3>1. Determine sensory levels for right and left sides.</h3>
                            <p>The sensory level is the most caudal, intact dermatome for both pin prick and light touch sensation.</p>
                        </div>

                        <div className="step">
                            <h3>2. Determine motor levels for right and left sides.</h3>
                            <p>Defined by the lowest key muscle function that has a grade of at least 3 (on supine testing), providing the key muscle functions rostral to that level are judged to be intact (graded as a 5).</p>
                            <p>Note: (a) in regions where there is no myotome to test, the motor level is presumed to be the same as the sensory level, if testable motor function above that level is also normal.</p>
                        </div>

                        <div className="step">
                            <h3>3. Determine the neurological level of injury (NLI).</h3>
                            <p>This refers to the most caudal segment of the cord with intact sensation and antigravity (3 or more) muscle function strength, provided that there is normal intact sensory and motor function rostrally thereafter.</p>
                            <p>The NLI is the most cephalad of the sensory and motor levels determined in steps 1 and 2.</p>
                        </div>

                        <div className="step">
                            <h3>4. Determine whether the injury is Complete or Incomplete.</h3>
                            <p>(i.e. absence or presence of sacral sparing)</p>
                            <p>If voluntary anal contraction = <strong>No</strong> AND all S4-5 sensory scores = <strong>0</strong> AND deep anal pressure = <strong>No</strong>, then injury is <strong>Complete</strong>.</p>
                            <p>Otherwise, injury is <strong>Incomplete</strong>.</p>
                        </div>

                        <div className="step">
                            <h3>5. Determine ASIA Impairment Scale (AIS) Grade:</h3>
                            <div className="flowchart">
                                <div className="flowchart-item">
                                    <strong>Is Injury Complete?</strong> If YES, AIS=A and stop record
                                </div>
                                <div className="flowchart-arrow">↓</div>
                                <div className="flowchart-item center">
                                    <strong>NO</strong>
                                </div>
                                <div className="flowchart-arrow">↓</div>
                                <div className="flowchart-item">
                                    <strong>Is Injury Motor Complete?</strong> If YES, AIS=B
                                </div>
                                <div className="flowchart-arrow">↓</div>
                                <div className="flowchart-item center">
                                    <strong>NO</strong>
                                </div>
                                <div className="flowchart-arrow">↓</div>
                                <div className="flowchart-decision">
                                    <strong>Are at least half (half or more) of the key muscles below the neurological level of injury graded 3 or better?</strong>
                                    <div className="decision-branches">
                                        <div className="branch">
                                            <strong>NO</strong>
                                            <div className="flowchart-arrow">↓</div>
                                            <div className="result">AIS=C</div>
                                        </div>
                                        <div className="branch">
                                            <strong>YES</strong>
                                            <div className="flowchart-arrow">↓</div>
                                            <div className="result">AIS=D</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="note">
                                <p>If sensation and motor function is normal in all segments, AIS=E</p>
                                <p><em>Note: AIS E is used in follow-up testing when an individual with a documented SCI has recovered normal function. If at initial testing no deficits are found, the individual is neurologically intact; the ASIA Impairment Scale does not apply.</em></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SCReference;